import { useMemo } from "react";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v.trim() !== "") return v;
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};
// Epoch-ms key for exact-instant matching (DocumentReference↔DiagnosticReport
// below) — deliberately NOT the same coarse "just the calendar day" grouping
// firstDate/dateOnly use for everything else (Observation↔DiagnosticReport
// linking, display/sort dates), since a same-day-only match there was
// wrongly attaching an unrelated document to a report that has no document
// of its own, whenever more than one DocumentReference happened to land on
// the same day (a real, observed case: three same-day DocumentReferences,
// only one of which was ever actually authored at the report's own exact
// effective timestamp). Date.parse (not a raw string compare) so a
// harmless format difference — offset notation, trailing milliseconds —
// can't defeat a real match.
const instantKey = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v !== "string") continue;
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) return String(ms);
  }
  return "";
};

// ── Lab component results ←  Observation ───────────────────────────────────

// "unknown" is a genuinely distinct state from "normal" — a value Epic sent
// with no interpretation AND nothing to compare against a reference range
// (missing value, missing range, or both). Collapsing that into "normal"
// used to make an entirely unassessed lab value look confirmed-normal in
// every downstream filter/stat (Normal/Abnormal filters, "Abnormal Tests"
// counts) — see isAbnormalLabStatus below, which every such consumer must
// use instead of a bare `!== "normal"` check.
export type LabResultStatus = "normal" | "low" | "high" | "critical_low" | "critical_high" | "unknown";

export interface LabComponentResult {
  id: string;
  componentName: string;
  value: string; // "" when unknown
  unit: string; // "" when unknown
  referenceRange: string; // "" when unknown
  status: LabResultStatus;
}

/** True only for a status that reflects a genuine, known abnormal reading —
 *  "unknown" (never assessed) must never count as abnormal any more than it
 *  should count as normal. */
export function isAbnormalLabStatus(status: LabResultStatus): boolean {
  return status === "low" || status === "high" || status === "critical_low" || status === "critical_high";
}

function computeStatus(f: Record<string, unknown>): LabResultStatus {
  const raw = String(f.interpretation ?? "").trim().toLowerCase();
  if (raw) {
    if (raw.includes("critical") && raw.includes("low")) return "critical_low";
    if (raw.includes("critical") && raw.includes("high")) return "critical_high";
    if (raw === "ll") return "critical_low";
    if (raw === "hh") return "critical_high";
    if (raw === "h" || raw.includes("high")) return "high";
    if (raw === "l" || raw.includes("low")) return "low";
    if (raw === "n" || raw.includes("normal")) return "normal";
  }
  const value = parseFloat(String(f.value_quantity ?? ""));
  const low = parseFloat(String(f.reference_range_low ?? ""));
  const high = parseFloat(String(f.reference_range_high ?? ""));
  const hasRange = !Number.isNaN(low) || !Number.isNaN(high);
  if (!Number.isNaN(value) && hasRange) {
    if (!Number.isNaN(low) && value < low) return "low";
    if (!Number.isNaN(high) && value > high) return "high";
    return "normal"; // a real value, actually measured against a real range, and it's within bounds
  }
  // No interpretation from the source, and nothing to compare the value
  // against (missing value, missing range, or both) — genuinely unassessed,
  // not confirmed normal.
  return "unknown";
}

function buildRange(f: Record<string, unknown>): string {
  const low = str(f.reference_range_low);
  const high = str(f.reference_range_high);
  if (low || high) return `${low || "?"} - ${high || "?"}`;
  // A qualitative range ("Negative", "See report") has no numeric low/high
  // at all — only this free-text field.
  return str(f.reference_range_text);
}

export function adaptLabComponent(record: ParsedRecord): LabComponentResult {
  const f = record.fields;
  return {
    id: record.id,
    componentName: firstStr(f.display_name) || "Lab value",
    value: firstStr(f.value_quantity, f.value_string, f.value_codeable_text),
    unit: str(f.value_unit),
    referenceRange: buildRange(f),
    status: computeStatus(f),
  };
}

// ── Lab reports ←  DiagnosticReport (non-imaging) ──────────────────────────

export type LabCategory = "blood" | "urine" | "microbiology" | "pathology" | "genetic" | "other";
export type LabReportStatus = "pending" | "preliminary" | "final" | "corrected" | "cancelled";

export interface LabReportRecord {
  id: string;
  testName: string;
  testCode: string; // "" when unknown
  category: LabCategory;
  collectionDate: string; // "" when unknown
  resultDate: string; // "" when unknown
  orderingProvider: string; // "" when unknown
  performingLab: string; // "" when unknown
  status: LabReportStatus;
  interpretation: string; // "" when unknown
  results: LabComponentResult[];
  criticalFlags: boolean;
  /** This DiagnosticReport's own id — its presentedForm attachment (fetched
   *  by epic_integration/service.py::fetch_binary) is the precise source
   *  for FilePane, preferred over documentReferenceId below. */
  diagnosticReportId: string;
  /** A DocumentReference authored at this report's exact effective/issued
   *  instant, if one exists — used only as a fallback when the report
   *  itself has no stored presentedForm file. Matched on the exact instant,
   *  not just the calendar day (a same-day-only match used to attach a
   *  completely unrelated document whenever more than one landed on the
   *  same day). null when no matching document was found. */
  documentReferenceId: string | null;
}

function mapLabCategory(category: string, name: string): LabCategory {
  const hay = `${category} ${name}`.toLowerCase();
  if (hay.includes("blood") || hay.includes("hematolog") || hay.includes("chemistry") || hay.includes("cbc") || hay.includes("panel")) {
    return "blood";
  }
  if (hay.includes("urine") || hay.includes("urinaly")) return "urine";
  if (hay.includes("microbiolog") || hay.includes("culture")) return "microbiology";
  if (hay.includes("patholog") || hay.includes("biopsy") || hay.includes("cytolog")) return "pathology";
  if (hay.includes("genetic") || hay.includes("genom")) return "genetic";
  return "other";
}

function mapReportStatus(status: string): LabReportStatus {
  const s = status.toLowerCase();
  if (s.includes("prelim") || s.includes("partial")) return "preliminary";
  if (s.includes("correct") || s.includes("amend")) return "corrected";
  if (s.includes("cancel") || s.includes("entered-in-error")) return "cancelled";
  if (s.includes("registered") || s.includes("pending")) return "pending";
  return "final";
}

// ── Imaging ─────────────────────────────────────────────────────────────
//
// EHR/data-feed-sourced imaging (DiagnosticReport records categorized as
// imaging) is deliberately NOT surfaced anywhere on this page any more — the
// Imaging tab now shows only patient-uploaded DICOM studies (see
// useImagingStudies.ts / GET /api/patient/imaging-studies), which have real
// pixel data, unlike these EHR reports (Epic/the hospital only ever attached
// the radiologist's report text here, never actual DICOM images — see the
// git history for the "media/imaging Epic-sync code removed" commit this
// followed). isImagingReport is kept only to keep an imaging-categorized
// DiagnosticReport OUT of the Lab Results list below, not to build a second
// imaging list from it.
function isImagingReport(f: Record<string, unknown>): boolean {
  const category = str(f.category).toLowerCase();
  const contentType = str(f.attachment_content_type).toLowerCase();
  const name = str(f.display_name).toLowerCase();
  return (
    category.includes("imag") ||
    category.includes("radiol") ||
    contentType.includes("image") ||
    contentType.includes("dicom") ||
    /x-?ray|mri|ct scan|ultrasound|sonogram|mammogram|radiograph|\bpet\b/.test(name)
  );
}

export interface DiagnosticsResult {
  labResults: LabReportRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDiagnosticsImaging(): DiagnosticsResult {
  const { records, isLoading, error, refresh } = useParsedRecords();

  return useMemo(() => {
    const observations = records.filter((r) => r.resourceType === "Observation").map(adaptLabComponent);
    const observationsByDate = new Map<string, LabComponentResult[]>();
    for (const r of records) {
      if (r.resourceType !== "Observation") continue;
      const d = firstDate(r.fields.effective_date, r.fields.issued);
      if (!d) continue;
      const comp = adaptLabComponent(r);
      observationsByDate.set(d, [...(observationsByDate.get(d) ?? []), comp]);
    }

    // Best-effort link from a lab/imaging report to the actual scanned
    // document (PDF/image) of that report: FHIR doesn't guarantee a direct
    // reference between DiagnosticReport and DocumentReference, so this
    // matches on the exact authored/effective INSTANT, not just the
    // calendar day — a same-day-only match was wrongly attaching an
    // unrelated document (a different report entirely) whenever more than
    // one DocumentReference landed on the same day, which real patient data
    // routinely does. First document seen for a given instant wins.
    const documentsByInstant = new Map<string, string>();
    for (const r of records) {
      if (r.resourceType !== "DocumentReference") continue;
      const key = instantKey(r.fields.authored_date);
      if (!key || documentsByInstant.has(key)) continue;
      documentsByInstant.set(key, r.id);
    }

    const reports = records.filter((r) => r.resourceType === "DiagnosticReport");

    const labResults: LabReportRecord[] = reports
      .filter((r) => !isImagingReport(r.fields))
      .map((r) => {
        const f = r.fields;
        const collectionDate = firstDate(f.effective_date, f.issued_date);
        const name = firstStr(f.display_name, f.code) || "Diagnostic report";
        return {
          id: r.id,
          testName: name,
          testCode: str(f.code),
          category: mapLabCategory(str(f.category), name),
          collectionDate,
          resultDate: firstDate(f.issued_date, f.effective_date),
          orderingProvider: firstStr(f.performer_display),
          performingLab: firstStr(f.performer_display),
          status: mapReportStatus(str(f.status)),
          interpretation: firstStr(f.interpreter_display),
          results: collectionDate ? observationsByDate.get(collectionDate) ?? [] : [],
          criticalFlags: (collectionDate ? observationsByDate.get(collectionDate) ?? [] : []).some(
            (c) => c.status === "critical_low" || c.status === "critical_high",
          ),
          diagnosticReportId: r.id,
          documentReferenceId: documentsByInstant.get(instantKey(f.effective_date, f.issued_date)) || null,
        };
      })
      .sort((a, b) => b.collectionDate.localeCompare(a.collectionDate));

    // Observations that never got attached to a report still deserve to show up.
    const attachedIds = new Set(labResults.flatMap((r) => r.results.map((c) => c.id)));
    const orphanObservations = observations.filter((c) => !attachedIds.has(c.id));
    if (orphanObservations.length > 0) {
      labResults.push({
        id: "loose-observations",
        testName: "Other lab values",
        testCode: "",
        category: "other",
        collectionDate: "",
        resultDate: "",
        orderingProvider: "",
        performingLab: "",
        status: "final",
        interpretation: "",
        results: orphanObservations,
        criticalFlags: orphanObservations.some(
          (c) => c.status === "critical_low" || c.status === "critical_high",
        ),
        diagnosticReportId: "", // synthetic entry, no backing DiagnosticReport
        documentReferenceId: null,
      });
    }

    return { labResults, isLoading, error, refresh };
  }, [records, isLoading, error, refresh]);
}
